import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import JsonLd from "@/components/feature/JsonLd";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { articles } from "@/mocks/articles";

const BASE = "https://kdove.cn";

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = articles.find((item) => String(item.id) === id);
  const [progress, setProgress] = useState(0);

  const headings = useMemo(() => {
    if (!article) return [] as { id: string; text: string }[];
    return article.content
      .filter((block) => block.type === "heading")
      .map((block, index) => ({
        id: `section-${index}`,
        text: block.type === "heading" ? block.text : "",
      }));
  }, [article]);

  const related = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((item) => item.id !== article.id && item.category === article.category)
      .concat(articles.filter((item) => item.id !== article.id && item.category !== article.category))
      .slice(0, 3);
  }, [article]);

  useEffect(() => {
    const onScroll = () => {
      const height = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(height > 0 ? Math.min(100, (window.scrollY / height) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useDocumentMeta({
    title: article ? `${article.title}｜吃瓜前排` : "文章不存在 - 吃瓜前排",
    description: article
      ? article.excerpt
      : "这篇内容暂时找不到，返回首页继续围观选美吃瓜现场。",
    keywords: article
      ? `选美吃瓜,${article.tags.join(",")},选美吃瓜现场`
      : "选美吃瓜,选美吃瓜现场",
    canonical: `${BASE}/article/${id}`,
    ogType: "article",
  });

  if (!article) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-content flex-col items-center justify-center px-4 py-20 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-file-warning-line text-2xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          这篇内容暂时找不到
        </h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          可能链接失效了。去栏目页逛逛，说不定能发现更有意思的名场面。
        </p>
        <Link
          to="/"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  let headingIndex = -1;

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: "吃瓜前排" },
      articleSection: article.categoryName,
      keywords: article.tags.join(","),
      mainEntityOfPage: `${BASE}/article/${article.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: article.categoryName,
          item: `${BASE}/category/${article.category}`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `${BASE}/article/${article.id}`,
        },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={structuredData} />
      <div
        className="fixed left-0 top-0 z-[60] h-0.5 bg-primary-500 transition-[width] duration-150"
        style={{ width: `${progress}%` }}
      />

      <article className="bg-background-50">
        <header className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: article.categoryName, to: `/category/${article.category}` },
                { label: "正文" },
              ]}
            />
            <Link
              to={`/category/${article.category}`}
              className="mt-5 inline-flex whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
            >
              {article.categoryName}
            </Link>
            <h1 className="mt-4 max-w-3xl font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {article.excerpt}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 items-center justify-center">
                  <i className="ri-user-line" />
                </span>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 items-center justify-center">
                  <i className="ri-calendar-line" />
                </span>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                阅读约 {article.readTime}
              </span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <figure className="overflow-hidden rounded-lg border border-background-200">
                <div className="h-56 w-full md:h-[380px]">
                  <img
                    src={article.cover}
                    alt={`${article.title} 现场配图`}
                    className="h-full w-full object-top"
                  />
                </div>
                <figcaption className="bg-background-100 px-5 py-3 text-xs text-foreground-500">
                  图片来源：{article.source}
                </figcaption>
              </figure>

              <div className="mt-8 space-y-6">
                {article.content.map((block) => {
                  if (block.type === "heading") {
                    headingIndex += 1;
                    return (
                      <h2
                        key={`h-${headingIndex}`}
                        id={`section-${headingIndex}`}
                        className="scroll-mt-24 pt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl"
                      >
                        <span className="mr-2 inline-block h-5 w-1 translate-y-0.5 rounded-full bg-primary-500" />
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "quote") {
                    return (
                      <blockquote
                        key={block.text}
                        className="rounded-lg border-l-4 border-accent-500 bg-accent-50 px-5 py-4 font-heading text-base leading-relaxed text-foreground-800 md:text-lg"
                      >
                        {block.text}
                      </blockquote>
                    );
                  }
                  if (block.type === "list") {
                    return (
                      <ul key={block.items.join("-")} className="space-y-3">
                        {block.items.map((item) => (
                          <li key={item} className="flex gap-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                            <span className="mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                              <i className="ri-checkbox-blank-circle-fill text-[8px]" />
                            </span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p key={block.text} className="text-sm leading-loose text-foreground-700 md:text-base">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-xs text-foreground-500">相关标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-28 space-y-6">
                <nav
                  aria-label="文章目录"
                  className="rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                    <span className="inline-flex h-4 w-4 items-center justify-center text-primary-600">
                      <i className="ri-list-check-2" />
                    </span>
                    本篇目录
                  </h2>
                  <ol className="mt-4 space-y-2.5">
                    {headings.map((heading, index) => (
                      <li key={heading.id}>
                        <a
                          href={`#${heading.id}`}
                          className="flex gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                        >
                          <span className="font-heading text-xs text-foreground-400">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          {heading.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>

                <div className="rounded-lg border border-background-200 bg-primary-950 p-5">
                  <h2 className="font-heading text-sm font-bold text-background-50">
                    想看更多同类现场？
                  </h2>
                  <p className="mt-2 text-xs leading-relaxed text-background-300">
                    进入「{article.categoryName}」栏目，按时间顺序补完这一季的完整时间线。
                  </p>
                  <Link
                    to={`/category/${article.category}`}
                    className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-medium text-foreground-950 transition-colors hover:bg-accent-400"
                  >
                    前往栏目
                    <span className="inline-flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            相关推荐
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}